const ALLOWED_PROTOCOLS = __SDK_ALLOWLIST__.allowedProtocols;

/**
 * Characters browsers strip or ignore while parsing a URL. Attackers use them to
 * smuggle `java<TAB>script:` past naive prefix checks, so they go before any test.
 */
// eslint-disable-next-line no-control-regex -- stripping control characters is the whole point.
const IGNORED = /[\u0000-\u0020\u007f-\u00a0\u200b-\u200f\ufeff]/g;

const DATA_IMAGE = /^data:image\/[a-z0-9.+-]+[;,]/;

export interface SafeUrlOptions {
  /** Permit `data:image/*` payloads. Only ever true for image sources. */
  allowData?: boolean;
}

/**
 * Conservative URL gate used before any href/src reaches the DOM.
 *
 * Accepts http(s), mailto, tel, protocol-relative, root-relative, relative and
 * fragment URLs. Everything else — notably `javascript:`, `vbscript:` and
 * `file:` — is rejected, as is `data:` unless an image payload is explicitly
 * allowed by the caller.
 */
export function isSafeUrl(value: unknown, options?: SafeUrlOptions): boolean {
  if (typeof value !== 'string') return false;
  const normalised = value.replace(IGNORED, '').toLowerCase();
  if (!normalised) return false;

  // Fragments and any form of relative path can never carry a scheme.
  const first = normalised.charAt(0);
  if (first === '#' || first === '/' || first === '?' || first === '.') return true;

  const colon = normalised.indexOf(':');
  if (colon === -1) return true;

  // A slash before the first colon means the colon lives inside a path segment,
  // e.g. `assets/a:b.png`, so there is no scheme at all.
  const slash = normalised.indexOf('/');
  if (slash !== -1 && slash < colon) return true;

  const protocol = normalised.slice(0, colon + 1);
  if (protocol === 'data:') {
    return options?.allowData === true && DATA_IMAGE.test(normalised);
  }
  return ALLOWED_PROTOCOLS.indexOf(protocol) !== -1;
}
